import { Box, Button, Stack, Tooltip } from '@mui/material'
import Drawer from '@mui/material/Drawer'
import { useQuery } from '@tanstack/react-query'
import { observer } from 'mobx-react'
import React, { FC, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import FormField from '../../../../components/Form/FormField'
import { getImageSrc } from '../../../../helpers/getImageSrc'
import { isOperator } from '../../../../services/AccountService'
import {
  getIncedentTypeReasons,
  getIncedentTypes,
} from '../../../../services/NewApiService'
import FormAutocomplete from '../../../SingleRequestPage/components/SingleRequestDrawer/elements/FormAutocomplete'
import TypedAccordion from '../../../SingleRequestPage/components/SingleRequestDrawer/elements/TypedAccordion'
import ContentTitle from './components/ContentTitle'
import DrawerHeader from './components/DrawerHeader'
import { defaultValues, resolver } from './consts/diagnosticDrawer.consts'
import { contentWrapperSx } from './consts/styles.consts'
import { createServiceRequestStore } from './stores/NewCreateServiceRequestStore'
import { DiagnosticFormType } from './types/NewDrawers.types'

interface NewDiagnosticRequestDrawerProps {
  serviceStore: any
}

const NewDiagnosticRequestDrawer: FC<
  NewDiagnosticRequestDrawerProps
> = ({}) => {
  const {
    isDiagnosticDrawerOpen,
    setIsDiagnosticDrawerOpen,
    setDiagnosticData,
    setIsCreateDrawerOpen,
    setIsCheckDrawerOpen,
    setSelectedRequest,
    setCreateFormData,
    setSearchParams,
    createFormData,
    setIncidentTypeId,
    setMalfunctionTypeId,
    resetAll,
    setResetAll,
    fromExistedRequest,
    setFromExistedRequest,
    setExistedRequestId,
    setActiveServiceOrder,
    setIsServerOrdersDrawerOpen,
  } = createServiceRequestStore

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isValid },
  } = useForm<DiagnosticFormType>({
    mode: 'onChange',
    defaultValues: {
      ...defaultValues,
    },
    resolver,
  })

  const { data: incedentTypes } = useQuery({
    queryKey: ['incedent-types'],
    queryFn: () => getIncedentTypes(),
    staleTime: Infinity,
    placeholderData: [],
  })

  const { data: incedentTypeReasons } = useQuery({
    queryKey: ['incedent-type-reasons'],
    queryFn: () => getIncedentTypeReasons(),
    staleTime: Infinity,
    placeholderData: [],
  })

  const onSubmit = (data: DiagnosticFormType) => {
    setDiagnosticData(data)
    setIsDiagnosticDrawerOpen(false)
    setIsServerOrdersDrawerOpen(true)
  }

  const onNotGuaranteeAccepted = () => {
    setDiagnosticData(watch())
    setIsDiagnosticDrawerOpen(false)
    setIsCheckDrawerOpen(true)
  }

  const onReset = () => reset(defaultValues)

  const onBack = () => {
    setIsDiagnosticDrawerOpen(false)
    !fromExistedRequest && setIsCreateDrawerOpen(true)
    onReset()
  }
  const onClose = () => {
    setSelectedRequest(null)
    setCreateFormData(null)
    setSearchParams({})
    setIsDiagnosticDrawerOpen(false)
    setResetAll()
    setFromExistedRequest(false)
    setExistedRequestId(null)
    setActiveServiceOrder(null)
  }

  useEffect(() => {
    if (resetAll) {
      onReset()
    }
  }, [resetAll])

  const isValidAndGuarantee =
    watch('incidentTypeId') === 'Неисправность по вине производителя'

  const isValidAndNotGuarantee =
    !!watch('incidentTypeId') &&
    watch('incidentTypeId') !== 'Неисправность по вине производителя'

  useEffect(() => {
    if (isDiagnosticDrawerOpen) {
      reset({
        ...defaultValues,
        equipmentId: createFormData?.equipmentId || 'Не заполнен',
        incidentTypeId: watch('incidentTypeId'),
        malfunction: {
          malfunctionTypeId: watch('malfunction.malfunctionTypeId'),
          malfunctionExplanation: watch('malfunction.malfunctionExplanation'),
        },
      })
    }
  }, [isValidAndGuarantee])

  useEffect(() => {
    if (createFormData?.equipmentId !== 'Не заполнен')
      setValue('equipmentId', createFormData?.equipmentId || 'Не заполнен')
  }, [createFormData?.equipmentId, isDiagnosticDrawerOpen])

  useEffect(() => {
    const currentId = incedentTypes?.find(
      (el: any) => el.name === watch('incidentTypeId'),
    )

    if (currentId) {
      setIncidentTypeId(currentId.id)
    }
  }, [watch('incidentTypeId')])

  useEffect(() => {
    const currentId = incedentTypeReasons?.find(
      (el: any) => el.name === watch('malfunction.malfunctionTypeId'),
    )

    if (currentId) {
      setMalfunctionTypeId(currentId.id)
    }
  }, [watch('malfunction.malfunctionTypeId')])

  const isEquipmentIdEmpty = watch('equipmentId') === 'Не заполнен'
  const showMalfunctionExplanation =
    watch('malfunction.malfunctionTypeId') === 'Другое'
  const notGuaranteeBtnDisabled =
    !watch('malfunction.malfunctionTypeId') ||
    (showMalfunctionExplanation &&
      !watch('malfunction.malfunctionExplanation').trim())

  return (
    <Drawer
      anchor={'right'}
      open={isDiagnosticDrawerOpen}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 380,
        },
      }}
    >
      <DrawerHeader
        title={`Заявка на ${!isOperator() ? 'обслуживание' : 'ремонт'}`}
        onBack={onClose}
        onClose={onClose}
      />
      <Stack
        component='form'
        onSubmit={handleSubmit(onSubmit)}
        sx={{ ...contentWrapperSx }}
        spacing={'14px'}
        justifyContent={'space-between'}
      >
        <Stack spacing={'14px'}>
          <Stack
            paddingX={'16px'}
            spacing={'14px'}
            marginBottom={'14px !important'}
          >
            <ContentTitle title={'Шаг 3. Диагностика неисправности'} />
            {!fromExistedRequest && (
              <>
                <Tooltip
                  placement='top'
                  title={watch('incidentTypeId')}
                  disableHoverListener={!watch('incidentTypeId')}
                >
                  <Box position={'relative'}>
                    <FormAutocomplete
                      control={control}
                      name='incidentTypeId'
                      fullWidth
                      label='Тип инцидента'
                      withoutTyping
                      options={incedentTypes?.map((r) => r.name) || []}
                      error={!watch('incidentTypeId')}
                      fullHeight
                      withClearBtn
                    />
                  </Box>
                </Tooltip>
                {!!watch('incidentTypeId') && (
                  <>
                    <Tooltip
                      placement='top'
                      title={watch('malfunction.malfunctionTypeId')}
                      disableHoverListener={
                        !watch('malfunction.malfunctionTypeId')
                      }
                    >
                      <Box position={'relative'}>
                        <FormAutocomplete
                          control={control}
                          fullWidth
                          name='malfunction.malfunctionTypeId'
                          options={
                            incedentTypeReasons?.map(
                              (reason: any) => reason.name,
                            ) ?? []
                          }
                          label='Неисправность со слов Абонента'
                          error={!watch('malfunction.malfunctionTypeId')}
                          withClearBtn
                        />
                      </Box>
                    </Tooltip>
                    {showMalfunctionExplanation && (
                      <FormField
                        control={control}
                        name='malfunction.malfunctionExplanation'
                        fullWidth
                        label='Описание'
                        multiline
                        rows={2}
                        error={
                          !watch('malfunction.malfunctionExplanation').trim()
                        }
                      />
                    )}
                  </>
                )}
              </>
            )}
          </Stack>
          {(isValidAndNotGuarantee || isValidAndGuarantee) && (
            <TypedAccordion summary={'Данные  установки'} customOpen>
              <Box
                position={'relative'}
                sx={{
                  marginTop: '16px!important',
                  img: {
                    position: 'absolute',
                    right: '20px',
                    top: '27%',
                  },
                }}
              >
                <FormField
                  control={control}
                  name='equipmentId'
                  fullWidth
                  disabled
                  label='ID оборудования'
                  onlyNumbers
                />

                {isEquipmentIdEmpty && <img src={getImageSrc('warn')} />}
              </Box>
            </TypedAccordion>
          )}
        </Stack>
        <Stack spacing={'14px'} marginX={'16px !important'}>
          {/* гарантийное продолжить */}
          {isValidAndGuarantee && !fromExistedRequest && (
            <Button
              type='submit'
              disabled={!isValid || notGuaranteeBtnDisabled}
              variant='contained'
            >
              Продолжить
            </Button>
          )}
          {/* негарантийное продолжить */}
          {isValidAndNotGuarantee && !fromExistedRequest && (
            <Button
              type='button'
              disabled={notGuaranteeBtnDisabled}
              variant='contained'
              onClick={onNotGuaranteeAccepted}
            >
              Продолжить
            </Button>
          )}

          <Button variant='outlined' onClick={onBack}>
            <img
              alt={'Назад'}
              src={getImageSrc('arrow-left-blue')}
              style={{ marginRight: '4px' }}
            />
            Назад
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  )
}

export default observer(NewDiagnosticRequestDrawer)
